/* Fresca Oral Care — theme interactions & motion */
(() => {
  'use strict';

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(pointer: fine)').matches;
  const root = window.Fresca || { routes: { cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change' } };

  /* ---------- Money ---------- */
  const formatMoney = (cents) => {
    const amount = (Number(cents) || 0) / 100;
    const n = amount.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: amount % 1 ? 2 : 0 });
    const fmt = root.moneyFormat || 'Rs. {{amount}}';
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, n);
  };

  let lenis = null;
  const drawer = $('[data-cart-drawer]');

  /* ---------- Scroll lock ----------
     body.is-locked is the public API (menu, drawer, preloader, section scripts). It is mirrored onto
     <html> (the real lock, see fresca.css) and pauses Lenis so wheel/touch can't move the page behind. */
  let locked = false;
  const syncLock = () => {
    const on = document.body.classList.contains('is-locked');
    if (on === locked) return;
    locked = on;
    document.documentElement.classList.toggle('is-locked', on);
    if (lenis) on ? lenis.stop() : lenis.start();
  };
  const setLock = (on) => { document.body.classList.toggle('is-locked', on); syncLock(); };
  new MutationObserver(syncLock).observe(document.body, { attributes: true, attributeFilter: ['class'] });

  /* ---------- Preloader → intro ---------- */
  let readyAt = 0;
  const readyQueue = [];
  const start = () => {
    document.body.classList.add('is-ready');
    if (readyAt) return;
    readyAt = performance.now();
    readyQueue.splice(0).forEach((f) => f());
  };
  // run fn once the hero intro (started by .is-ready) has had `ms` to play
  const afterIntro = (fn, ms) => {
    const go = () => setTimeout(fn, Math.max(0, readyAt + ms - performance.now()));
    readyAt ? go() : readyQueue.push(go);
  };
  const preloader = $('[data-preloader]');
  if (preloader && !reduced) {
    let seen = false;
    try { seen = sessionStorage.getItem('fr-seen') === '1'; } catch (e) {}
    const finish = () => { preloader.classList.add('is-done'); setTimeout(start, 200); try { sessionStorage.setItem('fr-seen', '1'); } catch (e) {} };
    if (seen) { preloader.remove(); start(); }
    else {
      setLock(true);
      const minTime = new Promise((r) => setTimeout(r, 1200));
      const loaded = new Promise((r) => (document.readyState === 'complete' ? r() : window.addEventListener('load', r, { once: true })));
      Promise.race([Promise.all([minTime, loaded]), new Promise((r) => setTimeout(r, 3000))]).then(() => {
        setLock(false);
        finish();
      });
    }
  } else {
    if (preloader) preloader.remove();
    requestAnimationFrame(start);
  }

  /* ---------- Smooth scroll (Lenis) ---------- */
  const initLenis = () => {
    if (lenis || reduced || !window.Lenis || window.Shopify?.designMode || root.smoothScroll === false) return;
    lenis = new window.Lenis({
      lerp: 0.12, smoothWheel: true, wheelMultiplier: 1,
      // overlays and inner scrollers keep native scrolling (the cart list, the mobile menu)
      prevent: (node) => node.matches('.fr-drawer, .fr-mmenu, [data-scroll-inner]')
    });
    if (locked) lenis.stop();
    if (window.gsap && window.ScrollTrigger) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add((t) => lenis.raf(t * 1000));
      window.gsap.ticker.lagSmoothing(0);
    } else {
      const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
  };

  /* ---------- Anchor links ---------- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href*="#"]');
    if (!a) return;
    const url = new URL(a.href, location.href);
    if (url.pathname !== location.pathname || !url.hash) return;
    const target = url.hash === '#top' ? document.body : document.querySelector(url.hash);
    if (!target) return;
    e.preventDefault();
    closeMenu();
    if (lenis) lenis.scrollTo(target, { offset: -70 });
    else window.scrollTo({ top: target === document.body ? 0 : target.getBoundingClientRect().top + scrollY - 70, behavior: reduced ? 'auto' : 'smooth' });
  });

  /* ---------- Header: sticky state, hide on scroll down, progress ---------- */
  // rAF-throttled; the page height is measured on resize only (reading it per scroll event forced layout)
  const header = $('[data-header]');
  const progress = $('.fr-progress i');
  let lastY = scrollY, maxScroll = 0, scrollTick = false;
  const measure = () => { maxScroll = document.documentElement.scrollHeight - innerHeight; };
  const onScroll = () => {
    scrollTick = false;
    const y = scrollY;
    if (header) {
      header.classList.toggle('is-scrolled', y > 20);
      const dy = y - lastY;
      // ignore tiny jitters (iOS momentum / Lenis easing) so the header doesn't flicker
      if (Math.abs(dy) > 6 || y <= 400) {
        header.classList.toggle('is-hidden', y > 400 && dy > 0 && !document.body.classList.contains('is-menu'));
        lastY = y;
      }
    }
    if (progress) progress.style.setProperty('--p', maxScroll > 0 ? Math.min(1, y / maxScroll).toFixed(4) : 0);
  };
  addEventListener('scroll', () => { if (!scrollTick) { scrollTick = true; requestAnimationFrame(onScroll); } }, { passive: true });
  if ('ResizeObserver' in window) new ResizeObserver(measure).observe(document.body);
  addEventListener('resize', measure);
  addEventListener('load', measure);
  measure(); onScroll();

  /* ---------- Mobile menu ---------- */
  const menuBtn = $('[data-menu-toggle]');
  function closeMenu() {
    if (!document.body.classList.contains('is-menu')) return;
    document.body.classList.remove('is-menu');
    if (!(drawer && drawer.classList.contains('is-open'))) setLock(false);
    menuBtn && menuBtn.setAttribute('aria-expanded', 'false');
  }
  menuBtn && menuBtn.addEventListener('click', () => {
    const open = !document.body.classList.contains('is-menu');
    document.body.classList.toggle('is-menu', open);
    if (header) header.classList.remove('is-hidden');
    setLock(open);
    menuBtn.setAttribute('aria-expanded', String(open));
  });

  /* ---------- Split headings into words ---------- */
  $$('[data-split]').forEach((el) => {
    const words = el.textContent.trim().split(/\s+/);
    el.setAttribute('aria-label', el.textContent.trim());
    el.innerHTML = words.map((w, i) => `<span class="fr-word" aria-hidden="true"><span style="--w:${i}">${w.replace(/&/g, "&amp;").replace(/</g, "&lt;")}</span></span>`).join(' ');
  });

  /* ---------- Reveal on scroll ---------- */
  const revealEls = $$('[data-reveal], [data-split]');
  if ('IntersectionObserver' in window && !reduced) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    revealEls.forEach((el) => io.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add('is-in'));
  }

  /* ---------- Counters ---------- */
  const counters = $$('[data-count]');
  const runCounter = (el) => {
    const end = parseFloat(el.dataset.count);
    const dec = parseInt(el.dataset.decimals || '0', 10);
    const dur = 1800;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - p, 4);
      el.textContent = (end * e).toFixed(dec);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  if (counters.length) {
    if (reduced || !('IntersectionObserver' in window)) counters.forEach((el) => (el.textContent = el.dataset.count));
    else {
      const cio = new IntersectionObserver((entries) => entries.forEach((en) => {
        if (en.isIntersecting) { runCounter(en.target); cio.unobserve(en.target); }
      }), { threshold: 0.5 });
      counters.forEach((el) => cio.observe(el));
    }
  }

  /* ---------- Hero bubbles canvas ---------- */
  const canvas = $('[data-bubbles]');
  if (canvas && !reduced) {
    const ctx = canvas.getContext('2d');
    let w, h, dpr, bubbles = [], visible = true, raf = 0, resizeT = 0;
    // two pre-rendered bubble sprites — drawImage is far cheaper than a gradient per bubble per frame
    const sprite = (edge) => {
      const s = document.createElement('canvas'); s.width = s.height = 64;
      const c = s.getContext('2d');
      const g = c.createRadialGradient(22, 22, 3, 32, 32, 30);
      g.addColorStop(0, 'rgba(255,255,255,.95)'); g.addColorStop(1, edge);
      c.fillStyle = g; c.beginPath(); c.arc(32, 32, 30, 0, Math.PI * 2); c.fill();
      c.lineWidth = 2; c.strokeStyle = 'rgba(255,255,255,.85)'; c.stroke();
      return s;
    };
    const sprites = [sprite('rgba(60,199,180,.38)'), sprite('rgba(60,199,180,.22)')];
    const resize = () => {
      dpr = Math.min(devicePixelRatio || 1, innerWidth < 768 ? 1.5 : 2);
      w = canvas.offsetWidth; h = canvas.offsetHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.round(Math.min(innerWidth < 768 ? 18 : 34, w / 36));
      bubbles = Array.from({ length: count }, () => spawn(true));
    };
    const spawn = (initial) => ({
      x: Math.random() * w,
      y: initial ? Math.random() * h : h + 20,
      r: 2 + Math.random() * 9,
      s: 0.25 + Math.random() * 0.8,
      drift: Math.random() * Math.PI * 2,
      a: 0.25 + Math.random() * 0.5,
      mint: Math.random() > 0.5
    });
    const draw = () => {
      raf = 0;
      if (!visible || document.hidden) return;
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < bubbles.length; i++) {
        const b = bubbles[i];
        b.y -= b.s; b.drift += 0.01; b.x += Math.sin(b.drift) * 0.35;
        if (b.y < -20) { bubbles[i] = spawn(false); continue; }
        ctx.globalAlpha = b.a;
        ctx.drawImage(sprites[b.mint ? 0 : 1], b.x - b.r, b.y - b.r, b.r * 2, b.r * 2);
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    const wake = () => { if (!raf && visible && !document.hidden) raf = requestAnimationFrame(draw); };
    new IntersectionObserver(([en]) => { visible = en.isIntersecting; wake(); }).observe(canvas);
    document.addEventListener('visibilitychange', wake);
    addEventListener('resize', () => { clearTimeout(resizeT); resizeT = setTimeout(resize, 150); });
    resize(); wake();
  }

  /* ---------- Hero mouse parallax ---------- */
  const stage = $('[data-hero-stage]');
  if (stage && finePointer && !reduced) {
    const layers = $$('[data-depth]', stage).map((el) => ({ el, d: parseFloat(el.dataset.depth), base: '' }));
    let tx = 0, ty = 0, cx = 0, cy = 0, raf = 0;
    // capture each layer's resting transform (rotation) once the intro is running
    setTimeout(() => layers.forEach((l) => { const t = getComputedStyle(l.el).transform; l.base = t === 'none' ? '' : t; }), 50);
    // only animate while the pointer is still easing toward its target (no idle rAF loop)
    const loop = () => {
      cx += (tx - cx) * 0.06; cy += (ty - cy) * 0.06;
      layers.forEach((l) => { l.el.style.transform = `translate3d(${(cx * l.d * 18).toFixed(2)}px, ${(cy * l.d * 18).toFixed(2)}px, 0) ${l.base}`; });
      raf = Math.abs(tx - cx) + Math.abs(ty - cy) > 0.002 ? requestAnimationFrame(loop) : 0;
    };
    $('[data-hero]').addEventListener('pointermove', (e) => {
      tx = (e.clientX / innerWidth - 0.5) * 2;
      ty = (e.clientY / innerHeight - 0.5) * 2;
      if (!raf) raf = requestAnimationFrame(loop);
    });
  }

  /* ---------- Card tilt ---------- */
  if (finePointer && !reduced) {
    $$('[data-tilt]').forEach((card) => {
      card.addEventListener('pointermove', (e) => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        card.style.transition = 'box-shadow .5s';
        card.style.transform = `perspective(1000px) rotateX(${-y * 7}deg) rotateY(${x * 9}deg) translateY(-6px)`;
      });
      card.addEventListener('pointerleave', () => {
        card.style.transition = '';
        card.style.transform = '';
      });
    });
  }

  /* ---------- Magnetic buttons ---------- */
  if (finePointer && !reduced) {
    $$('[data-magnetic]').forEach((el) => {
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        el.style.transform = `translate(${x * 0.22}px, ${y * 0.3}px)`;
      });
      el.addEventListener('pointerleave', () => (el.style.transform = ''));
    });
  }

  /* ---------- Custom cursor ---------- */
  const cursor = $('.fr-cursor');
  if (cursor && finePointer && !reduced) {
    let mx = -100, my = -100, x = -100, y = -100, raf = 0;
    const loop = () => {
      x += (mx - x) * 0.18; y += (my - y) * 0.18;
      cursor.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
      raf = Math.abs(mx - x) + Math.abs(my - y) > 0.2 ? requestAnimationFrame(loop) : 0;
    };
    addEventListener('pointermove', (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      mx = e.clientX; my = e.clientY; cursor.classList.add('is-on');
      if (!raf) raf = requestAnimationFrame(loop);
    }, { passive: true });
    document.addEventListener('pointerleave', () => cursor.classList.remove('is-on'));
    document.addEventListener('pointerover', (e) => {
      cursor.classList.toggle('is-hover', !!e.target.closest('a, button, [data-tilt], summary, input[type="range"]'));
    });
  }

  /* ---------- Before / after ---------- */
  $$('[data-ba]').forEach((ba) => {
    const range = $('.fr-ba__range', ba);
    const set = (v) => ba.style.setProperty('--pos', v + '%');
    if (!range) return;
    // the visitor always wins: any touch/drag/key stops the teaser sweep for good
    let touched = false;
    const grab = () => { touched = true; };
    ['pointerdown', 'touchstart', 'keydown'].forEach((t) => range.addEventListener(t, grab, { passive: true }));
    ba.addEventListener('pointerdown', grab, { passive: true });
    range.addEventListener('input', () => { touched = true; set(range.value); });
    // teaser sweep the first time it comes into view
    if (!reduced && 'IntersectionObserver' in window) {
      const o = new IntersectionObserver(([en]) => {
        if (!en.isIntersecting) return;
        o.disconnect();
        let t0 = 0;
        const sweep = (t) => {
          if (touched) return;
          t0 = t0 || t;
          const p = Math.min(1, (t - t0) / 2200);
          const v = 50 + Math.sin(p * Math.PI * 2) * 28 * (1 - p);
          set(v.toFixed(2)); range.value = v;
          if (p < 1) requestAnimationFrame(sweep);
        };
        setTimeout(() => { if (!touched) requestAnimationFrame(sweep); }, 500);
      }, { threshold: 0.6 });
      o.observe(ba);
    }
  });

  /* ---------- Tabs ---------- */
  $$('[data-tabs]').forEach((tabs) => {
    const btns = $$('[data-tab]', tabs);
    const ink = $('.fr-tabs__ink', tabs);
    const moveInk = (btn) => {
      if (!ink) return;
      ink.style.width = btn.offsetWidth + 'px';
      ink.style.transform = `translateX(${btn.offsetLeft}px)`;
    };
    btns.forEach((btn) => btn.addEventListener('click', () => {
      btns.forEach((b) => b.setAttribute('aria-selected', String(b === btn)));
      $$('[data-panel]', tabs).forEach((p) => {
        const on = p.dataset.panel === btn.dataset.tab;
        p.hidden = !on;
        p.classList.toggle('is-active', on);
        if (on) { p.classList.remove('is-entering'); void p.offsetWidth; p.classList.add('is-entering'); updateSteps(); }
      });
      moveInk(btn);
    }));
    const init = () => moveInk(btns.find((b) => b.getAttribute('aria-selected') === 'true') || btns[0]);
    init();
    addEventListener('resize', init);
    document.fonts && document.fonts.ready.then(init);
  });

  /* ---------- Step line progress (scroll-linked) ---------- */
  function updateSteps() {
    $$('[data-steps]').forEach((list) => {
      if (list.offsetParent === null) return;
      const r = list.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, (innerHeight * 0.75 - r.top) / r.height));
      list.style.setProperty('--line-p', p.toFixed(3));
    });
  }
  let stepTick = false;
  addEventListener('scroll', () => { if (!stepTick) { stepTick = true; requestAnimationFrame(() => { stepTick = false; updateSteps(); }); } }, { passive: true });
  updateSteps();

  /* ---------- Accordion (one open at a time) ---------- */
  $$('[data-accordion]').forEach((acc) => {
    const items = $$('details', acc);
    items.forEach((d) => d.addEventListener('toggle', () => {
      if (d.open) items.forEach((o) => o !== d && (o.open = false));
    }));
  });

  /* ---------- Countdown to midnight ---------- */
  $$('[data-countdown]').forEach((el) => {
    const h = $('[data-h]', el), m = $('[data-m]', el), s = $('[data-s]', el);
    const tick = () => {
      const now = new Date();
      const end = new Date(now); end.setHours(24, 0, 0, 0);
      let d = Math.max(0, Math.floor((end - now) / 1000));
      const pad = (n) => String(n).padStart(2, '0');
      h.textContent = pad(Math.floor(d / 3600)); d %= 3600;
      m.textContent = pad(Math.floor(d / 60));
      s.textContent = pad(d % 60);
    };
    tick(); setInterval(tick, 1000);
  });

  /* ---------- PDP: thumbs, qty, variant price ---------- */
  $$('[data-pdp-thumb]').forEach((t) => t.addEventListener('click', () => {
    const main = $('[data-pdp-main] img');
    if (main) { main.srcset = ''; main.src = t.dataset.pdpThumb; }
    $$('[data-pdp-thumb]').forEach((o) => o.classList.toggle('is-active', o === t));
  }));
  $$('[data-qty]').forEach((q) => {
    const input = $('input', q);
    $('[data-qty-minus]', q)?.addEventListener('click', () => (input.value = Math.max(1, (+input.value || 1) - 1)));
    $('[data-qty-plus]', q)?.addEventListener('click', () => (input.value = (+input.value || 1) + 1));
  });
  const variantSelect = $('[data-variant-select]');
  variantSelect && variantSelect.addEventListener('change', () => {
    const opt = variantSelect.selectedOptions[0];
    if (!opt) return;
    const box = $('[data-pdp-price]');
    const price = box && $('strong', box);
    if (price && opt.dataset.price) price.textContent = opt.dataset.price;
    // compare price / savings follow the variant when the options carry them
    // (data-compare="" means "not on sale" and hides the sale bits)
    if ('compare' in opt.dataset) {
      const onSale = !!opt.dataset.compare;
      const s = box && $('s', box), save = $('.fr-pdp__save'), seal = $('.fr-pdp__seal');
      if (s) { s.hidden = !onSale; if (onSale) s.textContent = opt.dataset.compare; }
      if (save) { save.hidden = !onSale; if (onSale && opt.dataset.save) save.textContent = opt.dataset.save; }
      if (seal) { seal.hidden = !onSale; const b = $('b', seal); if (b && opt.dataset.off) b.textContent = opt.dataset.off; }
    }
  });

  /* ==========================================================================
     Cart — real Shopify AJAX cart when a variant is linked, demo cart otherwise
     ========================================================================== */
  const itemsEl = $('[data-cart-items]');
  const subtotalEl = $('[data-cart-subtotal]');
  const countEls = $$('[data-cart-count]');
  const countLabel = $('[data-cart-count-label]');
  const checkoutBtn = $('[data-checkout]');
  const artSvg = (art) => `<svg viewBox="0 0 260 300" aria-hidden="true"><use href="#fr-${art || 'jar'}"/></svg>`;
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  let demo = [];
  try { demo = JSON.parse(localStorage.getItem('fr-demo-cart') || '[]'); } catch (e) { demo = []; }
  const saveDemo = () => { try { localStorage.setItem('fr-demo-cart', JSON.stringify(demo)); } catch (e) {} };
  let shopCart = null;

  // type: 'ok' (mint check) or 'info' (gold notice, for errors / hints)
  const toastEl = $('.fr-toast');
  let toastTimer = 0;
  const hideToast = () => { clearTimeout(toastTimer); toastEl && toastEl.classList.remove('is-on'); };
  const toast = (msg, type = 'ok') => {
    if (!toastEl || !msg) return;
    toastEl.textContent = msg;
    toastEl.classList.toggle('is-info', type === 'info');
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, type === 'info' ? 3600 : 2600);
  };

  const setCount = (n) => {
    countEls.forEach((el) => (el.textContent = n));
    if (countLabel) countLabel.textContent = n ? `(${n})` : '';
  };

  const render = () => {
    if (!itemsEl) return;
    const shopItems = shopCart ? shopCart.items : [];
    const rows = [
      ...shopItems.map((it) => ({ key: it.key, name: it.product_title, variant: it.variant_title && !/default/i.test(it.variant_title) ? it.variant_title : '', price: it.final_line_price, qty: it.quantity, img: it.image, art: /strip/i.test(it.handle) ? 'strips' : /spray/i.test(it.handle) ? 'spray' : 'jar', real: true })),
      ...demo.map((it) => ({ key: it.key, name: it.name, variant: 'Preview item', price: it.price * it.qty, qty: it.qty, art: it.art, real: false }))
    ];
    const count = rows.reduce((a, r) => a + r.qty, 0);
    const total = rows.reduce((a, r) => a + r.price, 0);
    setCount(count);
    subtotalEl && (subtotalEl.textContent = formatMoney(total));
    if (!rows.length) {
      itemsEl.innerHTML = `<div class="fr-drawer__empty">${artSvg('jar')}<p>Your cart is empty.<br>Let’s freshen things up!</p></div>`;
      return;
    }
    itemsEl.innerHTML = rows.map((r) => {
      const name = escapeHtml(r.name);
      return `
      <div class="fr-ditem" data-key="${escapeHtml(r.key)}" data-real="${r.real}">
        <div class="fr-ditem__img">${r.img ? `<img src="${escapeHtml(r.img)}" alt="" width="84" height="84" loading="lazy">` : artSvg(r.art)}</div>
        <div>
          <strong>${name}</strong>
          ${r.variant ? `<div class="fr-ditem__variant">${escapeHtml(r.variant)}</div>` : ''}
          <small>${formatMoney(r.price)}</small>
          <div class="fr-ditem__qty">
            <button type="button" data-q="-1" aria-label="Decrease quantity of ${name}"><svg class="fr-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12h14"/></svg></button>
            <span>${r.qty}</span>
            <button type="button" data-q="1" aria-label="Increase quantity of ${name}"><svg class="fr-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button>
          </div>
        </div>
        <button type="button" class="fr-ditem__remove" data-remove aria-label="Remove ${name}">Remove</button>
      </div>`;
    }).join('');
  };

  const fetchCart = () => fetch(`${root.routes.cart}.js`, { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((c) => { shopCart = c; render(); })
    .catch(() => render());

  // Drawer: modal dialog — focus moves in, Tab is trapped, focus returns to the trigger on close
  const panel = drawer && $('.fr-drawer__panel', drawer);
  let returnFocus = null;
  const isCartOpen = () => !!drawer && drawer.classList.contains('is-open');
  const openCart = () => {
    if (!drawer) return;
    if (!isCartOpen()) returnFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    setLock(true);
    hideToast(); // the drawer itself confirms the add
    requestAnimationFrame(() => { const c = $('.fr-drawer__close', drawer); c && c.focus({ preventScroll: true }); });
  };
  const closeCart = () => {
    if (!isCartOpen()) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (!document.body.classList.contains('is-menu')) setLock(false);
    if (returnFocus && document.contains(returnFocus) && drawer.contains(document.activeElement)) returnFocus.focus({ preventScroll: true });
    returnFocus = null;
  };

  $$('[data-cart-open]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); fetchCart(); openCart(); }));
  $$('[data-cart-close]').forEach((b) => b.addEventListener('click', closeCart));
  addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { if (isCartOpen()) closeCart(); else closeMenu(); return; }
    if (e.key !== 'Tab' || !isCartOpen() || !panel) return;
    const items = $$('a[href], button:not([disabled]), input:not([disabled]), select, [tabindex]:not([tabindex="-1"])', panel).filter((el) => el.getClientRects().length);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (!panel.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  itemsEl && itemsEl.addEventListener('click', (e) => {
    const row = e.target.closest('.fr-ditem');
    if (!row) return;
    const qBtn = e.target.closest('[data-q]');
    const rm = e.target.closest('[data-remove]');
    if (!qBtn && !rm) return;
    const key = row.dataset.key;
    const real = row.dataset.real === 'true';
    const current = +$('.fr-ditem__qty span', row).textContent;
    const qty = rm ? 0 : Math.max(0, current + Number(qBtn.dataset.q));
    if (real) {
      fetch(`${root.routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: qty }) })
        .then((r) => r.json()).then((c) => { shopCart = c; render(); });
    } else {
      const it = demo.find((d) => d.key === key);
      if (it) { it.qty = qty; demo = demo.filter((d) => d.qty > 0); saveDemo(); render(); }
    }
  });

  checkoutBtn && checkoutBtn.addEventListener('click', (e) => {
    if (!shopCart || !shopCart.item_count) {
      e.preventDefault();
      toast(demo.length ? 'Preview cart — link real products in the theme editor to enable checkout.' : 'Your cart is empty.', 'info');
    }
  });

  // Fly-to-cart animation
  const flyToCart = (fromEl, art) => {
    const cartBtn = $('.fr-cartbtn');
    if (!cartBtn || reduced) return;
    const a = fromEl.getBoundingClientRect();
    const b = cartBtn.getBoundingClientRect();
    const fly = document.createElement('div');
    fly.className = 'fr-fly';
    fly.innerHTML = artSvg(art);
    document.body.appendChild(fly);
    const sx = a.left + a.width / 2 - 35, sy = a.top + a.height / 2 - 40;
    const ex = b.left + b.width / 2 - 35, ey = b.top + b.height / 2 - 40;
    const anim = fly.animate([
      { transform: `translate(${sx}px, ${sy}px) scale(1) rotate(0)`, opacity: 1 },
      { transform: `translate(${(sx + ex) / 2}px, ${Math.min(sy, ey) - 140}px) scale(.8) rotate(-20deg)`, opacity: 1, offset: 0.5 },
      { transform: `translate(${ex}px, ${ey}px) scale(.2) rotate(15deg)`, opacity: .3 }
    ], { duration: 900, easing: 'cubic-bezier(.5,0,.3,1)' });
    anim.onfinish = () => {
      fly.remove();
      cartBtn.classList.remove('is-bump'); void cartBtn.offsetWidth; cartBtn.classList.add('is-bump');
    };
  };

  const markAdded = (btn) => {
    const label = $('span', btn);
    if (!label) return;
    const prev = label.textContent;
    btn.classList.add('is-added');
    label.textContent = 'Added ✓';
    setTimeout(() => { btn.classList.remove('is-added'); label.textContent = prev; }, 1600);
  };

  const addToShopify = (items) => fetch(`${root.routes.cartAdd}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items })
  }).then((r) => r.json().then((j) => { if (!r.ok) throw new Error(j.description || j.message || 'Could not add to cart'); return j; }));

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    const variants = (btn.dataset.variants || btn.dataset.variant || '').split(',').map((v) => v.trim()).filter(Boolean);
    const art = btn.dataset.art || 'jar';
    const host = btn.closest('.fr-card, .fr-combo, .fr-hero') || btn;
    const artEl = $('.fr-card__art, .fr-combo__item--jar', host) || btn;
    flyToCart(artEl, art);

    if (variants.length) {
      btn.disabled = true;
      addToShopify(variants.map((id) => ({ id: Number(id), quantity: 1 })))
        .then(() => fetchCart())
        .then(() => { markAdded(btn); toast(`${btn.dataset.name} added to cart`); setTimeout(openCart, 700); })
        .catch((err) => toast(err.message, 'info'))
        .finally(() => (btn.disabled = false));
    } else {
      const key = 'demo-' + btn.dataset.name;
      const found = demo.find((d) => d.key === key);
      if (found) found.qty += 1;
      else demo.push({ key, name: btn.dataset.name, price: Number(btn.dataset.price) || 0, qty: 1, art });
      saveDemo(); render();
      markAdded(btn);
      toast(`${btn.dataset.name} added to cart`);
      setTimeout(openCart, 700);
    }
  });

  // PDP form → AJAX add
  const pdpForm = $('#FrescaProductForm');
  pdpForm && pdpForm.addEventListener('submit', (e) => {
    if (e.submitter && e.submitter.name === 'checkout') return; // Buy now → native
    e.preventDefault();
    const fd = new FormData(pdpForm);
    const btn = e.submitter || $('button[type="submit"]', pdpForm);
    if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    // fly the right product art, from the sticky bar when that's what was tapped
    const pdpMain = $('[data-pdp-main]');
    const mainBox = pdpMain && pdpMain.getBoundingClientRect();
    const fromMain = mainBox && !btn.closest('[data-pdp-bar]') && mainBox.bottom > 0 && mainBox.top < innerHeight;
    flyToCart(fromMain ? pdpMain : btn, (pdpMain && pdpMain.dataset.art) || 'jar');
    btn.setAttribute('aria-busy', 'true');
    addToShopify([{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity')) || 1 }])
      .then(() => fetchCart())
      .then(() => { markAdded(btn); setTimeout(openCart, 700); })
      .catch((err) => toast(err.message, 'info'))
      .finally(() => btn.removeAttribute('aria-busy'));
  });

  // Buy now: add then go straight to checkout
  const buyNow = $('[data-buy-now]');
  buyNow && buyNow.addEventListener('click', (e) => {
    e.preventDefault();
    const fd = new FormData(pdpForm);
    addToShopify([{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity')) || 1 }])
      .then(() => (location.href = '/checkout'))
      .catch((err) => toast(err.message, 'info'));
  });

  setCount(root.cartCount || 0);
  fetchCart();

  /* ==========================================================================
     GSAP scroll choreography (progressive enhancement)
     ========================================================================== */
  const initGsap = () => {
    const { gsap, ScrollTrigger } = window;
    if (!gsap || !ScrollTrigger || reduced) return;
    gsap.registerPlugin(ScrollTrigger);

    // Hero: stage drifts and products spread as you scroll away.
    // The copy only drifts/softens on desktop (on phones .fr-hero__copy is display: contents) and never
    // fades so far that the buttons look disabled.
    const hero = $('[data-hero]');
    if (hero) {
      gsap.matchMedia().add('(min-width: 990px)', () => {
        gsap.to('.fr-hero__copy', { yPercent: -8, opacity: 0.55, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });
      });
      gsap.to('.fr-hero__ring', { scale: 1.25, opacity: 0, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } });
      // The disc grows in with a CSS `scale` transition. GSAP reads (and then pins) independent CSS
      // transforms when a tween is created, so wait for the intro to finish or the disc stays at 70%.
      const disc = $('.fr-hero__disc');
      if (disc) afterIntro(() => gsap.to(disc, { scale: 1.15, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true } }), 1700);
    }

    // Generic parallax helpers
    $$('[data-parallax]').forEach((el) => {
      gsap.fromTo(el, { yPercent: -parseFloat(el.dataset.parallax) }, { yPercent: parseFloat(el.dataset.parallax), ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true } });
    });

    // Marquee skews with scroll velocity, and always settles back upright when scrolling stops
    // (onUpdate only fires while scrolling, so a fast jump used to leave the text slanted).
    // The skew goes on each group, not on .fr-marquee__track: GSAP folds an element's CSS `translate`
    // into its inline transform, which froze the track's scroll offset and left a blank gap at the end.
    const skewers = $$('.fr-marquee__track > *').map((group) => gsap.quickTo(group, 'skewX', { duration: 0.5, ease: 'power3' }));
    if (skewers.length) {
      const maxSkew = finePointer ? 10 : 6;
      ScrollTrigger.create({ onUpdate: (self) => { const v = gsap.utils.clamp(-maxSkew, maxSkew, self.getVelocity() / -300); skewers.forEach((s) => s(v)); } });
      ScrollTrigger.addEventListener('scrollEnd', () => skewers.forEach((s) => s(0)));
    }

    // Combo: three products fly in and assemble into the bundle
    const combo = $('[data-combo]');
    if (combo) {
      const tl = gsap.timeline({ scrollTrigger: { trigger: combo, start: 'top 85%', end: 'center center', scrub: 1 } });
      tl.from('[data-combo-item="left"]', { xPercent: -140, yPercent: 40, rotate: -40, opacity: 0, ease: 'power3.out' }, 0)
        .from('[data-combo-item="right"]', { xPercent: 140, yPercent: 40, rotate: 40, opacity: 0, ease: 'power3.out' }, 0)
        .from('[data-combo-item="center"]', { yPercent: -80, scale: 0.6, opacity: 0, ease: 'back.out(1.6)' }, 0.15)
        .from('[data-combo-seal]', { scale: 0, rotate: -180, ease: 'back.out(2)' }, 0.55)
        .from('.fr-combo__leaf', { scale: 0, rotate: 90, stagger: 0.1 }, 0.4);
      // gentle outward lean once assembled. Explicit fromTo + immediateRender:false so it never captures
      // the timeline's -40/40 "from" rotation (that made the products tip over at mid-screen), and it
      // starts a little after the assembly ends so the two never fight over `rotate`.
      const lean = (sel, deg) => gsap.fromTo(sel, { rotate: 0 }, { rotate: deg, ease: 'none', immediateRender: false, scrollTrigger: { trigger: combo, start: 'center 38%', end: 'bottom top', scrub: true } });
      lean('[data-combo-item="left"]', -6);
      lean('[data-combo-item="right"]', 6);
    }

    // CTA products rise
    const cta = $('.fr-cta__art');
    if (cta) {
      gsap.from('.fr-cta__p', { y: 120, opacity: 0, stagger: 0.12, ease: 'power3.out', scrollTrigger: { trigger: cta, start: 'top 90%', end: 'top 50%', scrub: 1 } });
    }

    // Footer giant word slides
    const big = $('.fr-footer__big');
    if (big) gsap.from(big, { xPercent: -10, ease: 'none', scrollTrigger: { trigger: '.fr-footer', start: 'top bottom', end: 'bottom bottom', scrub: true } });

    initLenis();
    // re-measure trigger positions once late content (web fonts, images) settles
    const refresh = () => ScrollTrigger.refresh();
    if (document.readyState === 'complete') refresh(); else addEventListener('load', refresh, { once: true });
    document.fonts && document.fonts.ready.then(refresh);
  };

  // fresca.js is loaded before the CDN libraries (so cart/menu never wait on a slow CDN);
  // GSAP + Lenis are wired up once every deferred script has run.
  let booted = false;
  const boot = () => { if (booted) return; booted = true; initGsap(); initLenis(); };
  if ((window.gsap && window.ScrollTrigger) || document.readyState === 'complete') boot();
  else {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
    addEventListener('load', boot, { once: true });
  }

  /* ---------- Pause looping CSS animations in sections that are off-screen ---------- */
  if ('IntersectionObserver' in window && !reduced) {
    const pio = new IntersectionObserver((entries) => entries.forEach((en) => en.target.classList.toggle('fr-paused', !en.isIntersecting)), { rootMargin: '200px 0px' });
    const watch = (s) => { if (s && !s.querySelector('[data-header]')) pio.observe(s); };
    $$('.shopify-section').forEach(watch);
    document.addEventListener('shopify:section:load', (e) => watch(e.target));
  }

  /* Theme editor: keep things visible while editing */
  document.addEventListener('shopify:section:load', () => {
    $$('[data-reveal], [data-split]').forEach((el) => el.classList.add('is-in'));
    start();
  });
})();
